import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { GoogleMap, LoadScript, Marker, StandaloneSearchBox } from '@react-google-maps/api';
import styled from 'styled-components';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  Actions,
  BackIcon,
  BrandHeaderRow,
  BrandLogo,
  Button,
  Card,
  Field,
  Header,
  HeaderIconButton,
  HeaderText,
  Input,
  Message,
  Page,
  Shell,
  Subtitle,
  Title
} from '../components/PageTheme';
import {
  DISTANCE_UNIT_OPTIONS,
  DISTANCE_UNITS,
  convertDistanceToFeet,
  getDistanceUnit,
  getSavedDistanceUnit,
  getSavedDistanceValue
} from '../utils/distanceUnits';
import useBackNavigation from '../utils/useBackNavigation';

const MapPanel = styled(Card)`
  padding: 0;
  overflow: hidden;
`;

const HeaderActions = styled(Actions)`
  align-self: flex-start;
  margin-left: auto;
`;

const MapContainer = styled.div`
  height: min(58vh, 460px);
  min-height: 340px;
`;

const SearchBoxContainer = styled.div`
  position: relative;
  width: min(520px, calc(100vw - 4rem));
  margin: 1rem;

  input {
    width: 100%;
    min-height: 48px;
    padding: 0.75rem 0.95rem;
    border: 1px solid rgba(226, 232, 240, 0.28);
    border-radius: 18px;
    background: rgba(15, 23, 42, 0.9);
    color: #f8fafc;
    font-size: 1rem;
    font-weight: 800;
    outline: none;
    box-shadow: 0 18px 45px rgba(0, 0, 0, 0.22);

    &::placeholder {
      color: rgba(203, 213, 225, 0.58);
    }
  }
`;

const Form = styled.form`
  display: grid;
  grid-template-columns: minmax(180px, 1fr) minmax(260px, 320px) auto;
  gap: 0.85rem;
  align-items: end;
  margin-top: 1rem;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const DistanceControls = styled.div`
  display: grid;
  grid-template-columns: minmax(100px, 1fr);
  gap: 0.6rem;
`;

const UnitSelector = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  min-height: 40px;
`;

const UnitOption = styled.button`
  min-height: 34px;
  padding: 0.35rem 0.55rem;
  border: 1px solid ${({ $selected }) => ($selected ? 'rgba(251, 191, 36, 0.72)' : 'rgba(226, 232, 240, 0.14)')};
  border-radius: 999px;
  background: ${({ $selected }) => ($selected ? 'rgba(251, 191, 36, 0.18)' : 'rgba(15, 23, 42, 0.56)')};
  color: ${({ $selected }) => ($selected ? '#fbbf24' : 'rgba(226, 232, 240, 0.76)')};
  font: inherit;
  font-size: 0.76rem;
  font-weight: 950;
  letter-spacing: 0;
  text-transform: none;
  cursor: pointer;
`;

const UnitSeparator = styled.span`
  color: rgba(203, 213, 225, 0.46);
  font-weight: 950;
`;

const getGoogleMapsApiKey = () => {
  return window.RUNTIME_CONFIG?.googleMaps?.apiKey || process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
};

const getLocationPosition = (location) => {
  console.log('[EditLocation] getLocationPosition input:', location);

  if (!location) {
    console.log('[EditLocation] getLocationPosition: no location object');
    return null;
  }

  const rawPosition = location.location || location.coordinates || location;
  const lat = Number(rawPosition.lat ?? rawPosition.latitude);
  const lng = Number(rawPosition.lng ?? rawPosition.lon ?? rawPosition.long ?? rawPosition.longitude);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    console.log('[EditLocation] getLocationPosition: invalid coordinates', {
      rawPosition,
      lat,
      lng
    });
    return null;
  }

  const normalizedPosition = { lat, lng };
  console.log('[EditLocation] getLocationPosition output:', normalizedPosition);
  return normalizedPosition;
};

const EditLocation = () => {
  const { user } = useAuth();
  const { locationId } = useParams();
  const navigate = useNavigate();
  const goBack = useBackNavigation('/manage-locations');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationName, setLocationName] = useState('');
  const [requiredDistanceValue, setRequiredDistanceValue] = useState(DISTANCE_UNITS.feet.defaultValue);
  const [requiredDistanceUnit, setRequiredDistanceUnit] = useState('feet');
  const [mapCenter, setMapCenter] = useState({
    lat: 37.7749,
    lng: -122.4194
  });
  const [searchBox, setSearchBox] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const googleMapsApiKey = getGoogleMapsApiKey();

  const mapContainerStyle = {
    width: '100%',
    height: '100%'
  };
  const mapOptions = {
    clickableIcons: false,
    gestureHandling: 'greedy',
    streetViewControl: false,
    fullscreenControl: false
  };

  const handleSelectDistanceUnit = (nextUnit) => {
    setRequiredDistanceUnit(nextUnit);
    setRequiredDistanceValue(DISTANCE_UNITS[nextUnit].defaultValue);
  };

  useEffect(() => {
    console.log('[EditLocation] selectedLocation changed:', selectedLocation);
  }, [selectedLocation]);

  useEffect(() => {
    console.log('[EditLocation] mapCenter changed:', mapCenter);
  }, [mapCenter]);

  useEffect(() => {
    const fetchLocation = async () => {
      try {
        const response = await axios.get(`${window.RUNTIME_CONFIG.apiUrl}/locations`, {
          headers: { Authorization: `Bearer ${await user.getIdToken()}` }
        });
        console.log('[EditLocation] fetched locations response:', response.data);
        const savedLocation = response.data.find(location => location.id === locationId);
        console.log('[EditLocation] matched saved location:', {
          locationId,
          savedLocation
        });

        if (!savedLocation) {
          setError('Location not found');
          setLoading(false);
          return;
        }

        const position = getLocationPosition(savedLocation);
        setLocationName(savedLocation.name);
        setRequiredDistanceValue(getSavedDistanceValue(savedLocation));
        setRequiredDistanceUnit(getSavedDistanceUnit(savedLocation));

        if (position) {
          setSelectedLocation(position);
          setMapCenter(position);
        } else {
          setError('Saved location is missing valid coordinates. Tap the map to set the pin again.');
        }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching location:', error);
        setError('Failed to fetch location');
        setLoading(false);
      }
    };

    fetchLocation();
  }, [locationId, user]);

  const setPinLocation = (nextLocation) => {
    console.log('[EditLocation] setPinLocation:', nextLocation);
    setSelectedLocation(nextLocation);
    setMapCenter(nextLocation);
  };

  const handleMapClick = (event) => {
    console.log('[EditLocation] map interaction event:', {
      hasEvent: Boolean(event),
      hasLatLng: Boolean(event?.latLng),
      latLng: event?.latLng
        ? {
            lat: event.latLng.lat(),
            lng: event.latLng.lng()
          }
        : null
    });

    if (event && event.latLng) {
      const nextLocation = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng()
      };

      setPinLocation(nextLocation);
    }
  };

  const handlePlacesChanged = () => {
    console.log('[EditLocation] places changed. hasSearchBox:', Boolean(searchBox));

    if (searchBox) {
      const places = searchBox.getPlaces();
      console.log('[EditLocation] search places:', places);
      if (places && places.length > 0) {
        const place = places[0];
        const nextLocation = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng()
        };
        setPinLocation(nextLocation);
      }
    }
  };

  const handleSaveLocation = async (event) => {
    event.preventDefault();

    if (!selectedLocation || !locationName.trim()) {
      setError('Please set a location and provide a name');
      return;
    }

    const requiredDistance = convertDistanceToFeet(requiredDistanceValue, requiredDistanceUnit);

    if (!requiredDistance) {
      setError('Please enter a valid radius');
      return;
    }

    setSaving(true);
    setError('');

    try {
      console.log('[EditLocation] saving location payload:', {
        locationId,
        location: selectedLocation,
        name: locationName.trim(),
        requiredDistance,
        requiredDistanceValue: Number(requiredDistanceValue),
        requiredDistanceUnit
      });

      await axios.put(
        `${window.RUNTIME_CONFIG.apiUrl}/locations/${locationId}`,
        {
          location: selectedLocation,
          name: locationName.trim(),
          requiredDistance,
          requiredDistanceValue: Number(requiredDistanceValue),
          requiredDistanceUnit
        },
        {
          headers: { Authorization: `Bearer ${await user.getIdToken()}` }
        }
      );

      navigate('/manage-locations');
    } catch (error) {
      console.error('Error updating location:', error);
      setError(error.response?.data?.message || 'Failed to update location');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Page>
        <Shell $wide>
          <Card>Loading location...</Card>
        </Shell>
      </Page>
    );
  }

  return (
    <Page>
      <Shell $wide>
        <Header>
          <HeaderText>
            <BrandHeaderRow>
              <BrandLogo />
              <HeaderActions>
                <HeaderIconButton type="button" aria-label="Back" onClick={goBack}>
                  <BackIcon aria-hidden="true" />
                </HeaderIconButton>
              </HeaderActions>
            </BrandHeaderRow>
            <Title>Edit location.</Title>
            <Subtitle>Move the pin, rename the place, or adjust how close counts as arrived.</Subtitle>
          </HeaderText>
        </Header>

        {googleMapsApiKey ? (
          <MapPanel>
            <MapContainer>
              <LoadScript
                googleMapsApiKey={googleMapsApiKey}
                version="quarterly"
                libraries={['places']}
              >
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={mapCenter}
                  zoom={16}
                  options={mapOptions}
                  onLoad={mapInstance => {
                    console.log('[EditLocation] GoogleMap loaded:', {
                      center: mapInstance.getCenter()
                        ? {
                            lat: mapInstance.getCenter().lat(),
                            lng: mapInstance.getCenter().lng()
                          }
                        : null
                    });
                  }}
                  onClick={handleMapClick}
                >
                  <StandaloneSearchBox
                    onLoad={ref => {
                      console.log('[EditLocation] search box loaded:', ref);
                      setSearchBox(ref);
                    }}
                    onPlacesChanged={handlePlacesChanged}
                  >
                    <SearchBoxContainer>
                      <input type="text" placeholder="Search for a location" />
                    </SearchBoxContainer>
                  </StandaloneSearchBox>

                  {selectedLocation && (
                    <Marker
                      key={`${selectedLocation.lat}-${selectedLocation.lng}`}
                      position={selectedLocation}
                      onLoad={() => console.log('[EditLocation] marker loaded at:', selectedLocation)}
                    />
                  )}
                </GoogleMap>
              </LoadScript>
            </MapContainer>
          </MapPanel>
        ) : (
          <Message $error>Google Maps API key is not configured. Please check your environment configuration.</Message>
        )}

        <Form onSubmit={handleSaveLocation}>
          <Field>
            Location name
            <Input
              type="text"
              value={locationName}
              onChange={(event) => setLocationName(event.target.value)}
              placeholder="Location name"
              required
            />
          </Field>
          <Field>
            Radius
            <DistanceControls>
              <Input
                type="number"
                aria-label="Radius value"
                min={getDistanceUnit(requiredDistanceUnit).min}
                step={getDistanceUnit(requiredDistanceUnit).step}
                value={requiredDistanceValue}
                onChange={(event) => setRequiredDistanceValue(event.target.value)}
                placeholder="Distance"
                required
              />
              <UnitSelector aria-label="Radius unit">
                {DISTANCE_UNIT_OPTIONS.map((option, index) => (
                  <React.Fragment key={option.value}>
                    {index > 0 && <UnitSeparator aria-hidden="true">-</UnitSeparator>}
                    <UnitOption
                      type="button"
                      $selected={requiredDistanceUnit === option.value}
                      aria-pressed={requiredDistanceUnit === option.value}
                      aria-label={`Use ${option.label}`}
                      onClick={() => handleSelectDistanceUnit(option.value)}
                    >
                      {option.label}
                    </UnitOption>
                  </React.Fragment>
                ))}
              </UnitSelector>
            </DistanceControls>
          </Field>
          <Button type="submit" disabled={saving || !selectedLocation}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Form>

        {error && <Message $error>{error}</Message>}
      </Shell>
    </Page>
  );
};

export default EditLocation;
